<template>
  <OptimizationBaseDialog
    v-model="visibility"
    :optimization="props.optimization"
    :person="props.person">
    <div class="relative">
      <div
        @click="showAccount()"
        class="mb-4 flex w-full items-center gap-3 rounded border p-4 hover:cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
        <div class="flex flex-col">
          <TextMultiline
            v-for="software_license in licenses"
            :key="software_license.name"
            :text="software_license.name" />
        </div>
        <div class="ml-auto mr-4">
          <SmTooltip :content="i18n.t('showAccount')">
            <ExternalLink :size="18" class="cursor-pointer !text-contrast" />
          </SmTooltip>
        </div>
      </div>
      <ArrowDown
        :size="26"
        stroke-width="3"
        class="absolute-center circular-fill text-gray-400 dark:text-gray-300" />
      <div class="mb-4 flex w-full items-center gap-3 rounded border p-4">
        <div class="flex flex-col">
          <TextMultiline
            v-for="softwareLicense in softwareLicensesToAdd"
            :key="softwareLicense.name"
            :text="softwareLicense.name" />
        </div>
      </div>
    </div>

    <SmDialogMessage
      type="warning"
      :visible="!data.writeback_enabled"
      :message="
        i18n.t('rightsizing.writebackDisabled', {
          softwareLicenses: virtualSoftwareLicenses
            .map(
              (softwareLicense: VirtualSoftwareLicense) => softwareLicense.name
            )
            .join(', '),
        })
      " />

    <!-- Footer -->
    <template #footer>
      <SmButton
        v-if="props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('unhide', props.optimization._id)">
        {{ i18n.t('unhideOptimization') }}
      </SmButton>
      <SmButton
        v-else-if="!props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('hide', props.optimization._id)">
        {{ i18n.t('views.optimizations.table.actions.hideOptimization') }}
      </SmButton>
      <SmButton
        v-if="!props.optimization.deprecated"
        :disabled="!data.writeback_enabled"
        @click="executeChanges()">
        {{ i18n.t('execute') }}
      </SmButton>
    </template>
  </OptimizationBaseDialog>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import {
    EnrichedOptimizations,
    OptimizationStore,
  } from '@/stores/optimization/optimization.store'
  import {
    Account,
    SoftwareLicense,
    SoftwareLicensesService,
    VirtualSoftwareLicense,
  } from '@/client'
  import OptimizationBaseDialog from './OptimizationBaseDialog.vue'
  import { ArrowDown } from 'lucide-vue-next'
  import { OptimizationRightsizingData } from '@/client/models/OptimizationRightsizingData'
  import { sendToast } from '@/components/sm/SmNotification'
  import { AccountStore } from '@/stores/account/account.store'
  import { ExternalLink } from 'lucide-vue-next'

  const i18n = useI18n()

  const props = defineProps({
    optimization: {
      type: Object as PropType<EnrichedOptimizations>,
      required: true,
    },
    person: {
      type: Object as PropType<Account>,
      required: true,
    },
  })

  const emit = defineEmits(['hide', 'unhide', 'openAccountDrawer'])
  const visibility = defineModel({
    type: Boolean,
    required: true,
  })

  const data = props.optimization.data as OptimizationRightsizingData

  const softwareLicensesToAdd = computed(() => {
    return data.software_licenses_to_add ?? []
  })

  const virtualSoftwareLicenses = computed(() => {
    return (data.software_licenses_to_add ?? []).filter(
      (softwareLicense) => !softwareLicense.software_license_id
    )
  })

  // ════════════════════════════════════════════════════════════════════════════
  //  Load Data
  // ════════════════════════════════════════════════════════════════════════════

  const licenses = ref<SoftwareLicense[]>([])

  const idsToRemove = computed(() =>
    Object.entries(data.software_licenses_to_remove ?? {}).map(([key]) => key)
  )

  onMounted(async () => {
    if (idsToRemove.value.length) {
      licenses.value =
        await SoftwareLicensesService.getSoftwareLicensesApiV1SoftwareSoftwareLicensesGet(
          {
            idIn: idsToRemove.value,
          }
        )
    }
  })

  async function showAccount() {
    const info = data as OptimizationRightsizingData
    let account =
      (await AccountStore.getAccountById(info.software_account_id)) ?? null

    if (account) emit('openAccountDrawer', account)
    else
      sendToast(
        i18n.t('errorOccurred'),
        i18n.t('noAccountFound'),
        'error',
        5000
      )
  }
  // ----------------------------------------------------------------------
  // Execute changes
  // ----------------------------------------------------------------------

  const loadingExecute = ref(false)
  function executeChanges() {
    loadingExecute.value = true
    try {
      OptimizationStore.executeOptimization(props.optimization._id).then(
        (res) => {
          if (res.error) {
            sendToast(i18n.t('AccountUpdateError'), undefined, 'error')
          } else {
            OptimizationStore.refreshOptimizationCacheFromApi({}, false, false)
            sendToast(i18n.t('AccountUpdateSuccess'), undefined, 'success')
            visibility.value = false
          }
        }
      )
    } finally {
      loadingExecute.value = false
    }
  }
</script>
<style lang="scss" scoped>
  .absolute-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .circular-fill {
    background-color: var(--sm-elevation);
    border-radius: 100%;
    border: 1px solid theme('colors.gray.200');
    padding: 3px;
  }
</style>
